import {createSlice, isAnyOf} from '@reduxjs/toolkit'
import {
  loginAsync,
  validateUserSessionGetAsync,
  registrationAsync,
  requestPasswordForgetAsync,
  userOauthLoginAsync,
  verifyForgetPasswordAsync,
  verifyRegistrationAsync,
  userLoginUsingInstanceAsync,
  getInitialSyncStatusAsync,
  googleLoginAsync,
} from '../actions/authActions'
import {USER_DATA} from 'src/app/_interfaces/auth.interface'
import {accessSessionTokenKeyName, refreshSessionTokenKeyName} from 'src/utils/constants'
import {resetImpersonationForFreshLogin} from 'src/utils/impersonationTokens'
import {clearStaleLogoutGate} from 'src/utils/sessionExpiredLogout'
import {getMenuList} from '../actions/manageUserActions'

type LOGIN_PAYLOAD = {
  isLoggedIn: boolean
  userData: USER_DATA | null
  status?: number
}

type IsLoggedIn_PAYLOAD = {
  isLoggedIn: boolean
}

type InitialSyncProgressDetail = {
  display_name: string
  remaining_qty: number
  total_qty_fetched: number
  total_qty_available_in_channel: number
}

const initialState: {
  authLoading: boolean
  isLoggedIn: boolean
  userDetail: null | USER_DATA
  status?: number
  forgetPasswordToken: string | null
  forgetPasswordTokenLoading: boolean
  shopifyConnectFirstTimeMessageShow: boolean
  firstTimeMessage: string
  initialSync: boolean | null
  initialPlatformSyncMessage: string
  initialSyncSteps: {
    storeConnected: boolean
    productsFetched: number | null
    inventoryFetched: boolean | null
    settingsFetched: boolean | null
  }
  initialSyncProgressDetail: InitialSyncProgressDetail[]
  menuList: any[]
} = {
  authLoading: false,
  isLoggedIn: false,
  userDetail: null,
  status: undefined,
  forgetPasswordToken: '',
  forgetPasswordTokenLoading: false,
  shopifyConnectFirstTimeMessageShow: false,
  firstTimeMessage: 'Please go to Add new channel and Connect your Destination account.',
  initialSync: null,
  initialPlatformSyncMessage: '',
  initialSyncSteps: {
    storeConnected: false,
    productsFetched: null,
    inventoryFetched: null,
    settingsFetched: null,
  },
  initialSyncProgressDetail: [],
  menuList: [],
}

const authSlice = createSlice({
  name: 'authSlice',
  initialState,
  extraReducers: (builder) => {
    // registration end
    builder.addMatcher(isAnyOf(registrationAsync.pending), (state) => {
      console.log('registrationAsync is in pending state ')
    })
    builder.addMatcher(isAnyOf(registrationAsync.rejected), (state) => {
      console.log('registrationAsync is in rejected state ')
    })
    builder.addMatcher(isAnyOf(registrationAsync.fulfilled), (state, action) => {
      console.log('register fullfiled')
      state.authLoading = false
    })
    // registration end
    // login start
    builder.addMatcher(isAnyOf(loginAsync.pending, userOauthLoginAsync.pending), (state) => {
      console.log('loginAsync is in pending state ')
    })
    builder.addMatcher(isAnyOf(loginAsync.rejected, userOauthLoginAsync.rejected), (state) => {
      console.log('loginAsync is in rejected state ')
    })
    builder.addMatcher(
      isAnyOf(loginAsync.fulfilled, userLoginUsingInstanceAsync.fulfilled),
      (state, action) => {
        // state.userDetail = action.payload.data
        if (action?.payload?.access) {
          resetImpersonationForFreshLogin()
          localStorage.setItem(accessSessionTokenKeyName, action?.payload?.access)
          localStorage.setItem(refreshSessionTokenKeyName, action?.payload?.refresh)
          state.isLoggedIn = true
        }
        clearStaleLogoutGate()
        state.status = action?.payload?.payload?.status
      }
    )

    builder.addMatcher(isAnyOf(googleLoginAsync.fulfilled), (state, action) => {
      const access = action?.payload?.data?.access
      const refresh = action?.payload?.data?.refresh
      if (action?.payload?.status_code === 200 && access && refresh) {
        resetImpersonationForFreshLogin()
        localStorage.setItem(accessSessionTokenKeyName, access)
        localStorage.setItem(refreshSessionTokenKeyName, refresh)
        state.isLoggedIn = true
      }
      clearStaleLogoutGate()
    })

    builder.addMatcher(isAnyOf(userOauthLoginAsync.fulfilled), (state, action) => {
      if (action?.payload?.access) {
        resetImpersonationForFreshLogin()
        localStorage.setItem(accessSessionTokenKeyName, action.payload.access)
        localStorage.setItem(refreshSessionTokenKeyName, action.payload.refresh)
        state.isLoggedIn = true
      }
      clearStaleLogoutGate()
    })

    // mfa login  start
    builder.addMatcher(
      isAnyOf(validateUserSessionGetAsync.pending, userOauthLoginAsync.pending),
      (state) => {
        console.log('validateUserSessionAsync is in pending state ')
      }
    )
    builder.addMatcher(
      isAnyOf(validateUserSessionGetAsync.rejected, userOauthLoginAsync.rejected),
      (state) => {
        console.log('validateUserSessionAsync is in rejected state ')
      }
    )
    builder.addMatcher(
      isAnyOf(validateUserSessionGetAsync.fulfilled, userLoginUsingInstanceAsync.fulfilled),
      (state, action) => {
        state.userDetail = action.payload.data
        if (action?.payload?.access) {
          resetImpersonationForFreshLogin()
          localStorage.setItem(accessSessionTokenKeyName, action?.payload?.access)
          localStorage.setItem(refreshSessionTokenKeyName, action?.payload?.refresh)
          state.isLoggedIn = true
        }
        clearStaleLogoutGate()
        state.status = action?.payload?.payload?.status
        console.log(action?.payload, 'payload')
      }
    )
    // maf login  end

    //forgot passward
    builder.addMatcher(isAnyOf(requestPasswordForgetAsync.pending), (state) => {
      console.log('requestPasswordForgetAsync is in pending state ')
    })
    builder.addMatcher(isAnyOf(requestPasswordForgetAsync.rejected), (state) => {
      console.log('requestPasswordForgetAsync is in rejected state ')
    })
    builder.addMatcher(isAnyOf(requestPasswordForgetAsync.fulfilled), (state, action) => {
      console.log('requestPasswordForgetAsync in fulfilled')
      state.authLoading = false
      state.menuList = action.payload
    })

    //verifyuserforpassword
    builder.addMatcher(isAnyOf(verifyForgetPasswordAsync.pending), (state) => {
      state.forgetPasswordTokenLoading = true
    })
    builder.addMatcher(isAnyOf(verifyForgetPasswordAsync.rejected), (state) => {
      state.forgetPasswordTokenLoading = true
    })
    builder.addMatcher(isAnyOf(verifyForgetPasswordAsync.fulfilled), (state, action) => {
      state.forgetPasswordTokenLoading = false
    })

    // Initial Sync Status
    builder.addMatcher(isAnyOf(getInitialSyncStatusAsync.pending), (state) => {
      console.log('Fetching Initial Sync Status...')
    })
    builder.addMatcher(isAnyOf(getInitialSyncStatusAsync.fulfilled), (state, action) => {
      const data = action.payload?.data
      const initialSyncStatus = data?.initial_platform_sync
      const initialSyncMessage = data?.initial_platform_sync_message
      state.initialSync = initialSyncStatus
      state.initialPlatformSyncMessage = initialSyncMessage
      state.initialSyncProgressDetail = Array.isArray(data?.initial_platform_sync_progress_detail)
        ? data.initial_platform_sync_progress_detail
        : []

      const msg: string = (initialSyncMessage || '').toLowerCase()
      state.initialSyncSteps.storeConnected = true

      if (typeof data?.products_fetched_count === 'number') {
        state.initialSyncSteps.productsFetched = data.products_fetched_count
      } else if (msg.includes('product')) {
        const match = msg.match(/(\d[\d,]*)/)
        state.initialSyncSteps.productsFetched = match
          ? parseInt(match[1].replace(/,/g, ''), 10)
          : 0
      }

      if (typeof data?.inventory_fetched === 'boolean') {
        state.initialSyncSteps.inventoryFetched = data.inventory_fetched
      } else {
        state.initialSyncSteps.inventoryFetched = msg.includes('inventor') ? true : null
      }

      if (typeof data?.settings_fetched === 'boolean') {
        state.initialSyncSteps.settingsFetched = data.settings_fetched
      } else {
        state.initialSyncSteps.settingsFetched = msg.includes('setting') ? true : null
      }

      if (initialSyncStatus === true) {
        state.initialSyncSteps.productsFetched = state.initialSyncSteps.productsFetched ?? 0
        state.initialSyncSteps.inventoryFetched = true
        state.initialSyncSteps.settingsFetched = true
      }
    })
    builder.addMatcher(isAnyOf(getInitialSyncStatusAsync.rejected), (state, action) => {
      state.initialSync = null
      state.initialPlatformSyncMessage = ''
      state.initialSyncProgressDetail = []
    })

    // menuList for the sidebarMenu
    builder.addMatcher(isAnyOf(getMenuList.pending), (state) => {
      state.authLoading = true
      console.log('getMenuList is in pending state ')
    })
    builder.addMatcher(isAnyOf(getMenuList.rejected), (state, action) => {
      console.log('getMenuList rejected', action.error)
      state.authLoading = false
    })
    builder.addMatcher(isAnyOf(getMenuList.fulfilled), (state, action) => {
      console.log('getMenuList in fulfilled', action.payload)
      state.authLoading = false
      state.menuList = action.payload?.data || []
    })
  },
  reducers: {
    // use this reducer for login logout
    setLoggingData: (state, action: {payload: LOGIN_PAYLOAD; type: string}) => {
      state.isLoggedIn = action.payload?.isLoggedIn
      state.userDetail = action.payload.userData
    },

    setIsLoggedIn: (state, action: {payload: IsLoggedIn_PAYLOAD; type: string}) => {
      state.isLoggedIn = action.payload.isLoggedIn
      if (action.payload.isLoggedIn) {
        clearStaleLogoutGate()
      }
    },

    setForgetPasswordToken: (state, action: {payload: {token: string}; type: string}) => {
      state.forgetPasswordToken = action?.payload?.token
    },

    setShopifyConnectFirstTimeMessageShow: (
      state,
      action: {payload: {show: boolean; message?: string}}
    ) => {
      state.shopifyConnectFirstTimeMessageShow = action.payload.show
      state.firstTimeMessage = action.payload.message ?? ''
    },

    setInitialSyncStatus: (state, action: {payload: boolean | null}) => {
      state.initialSync = action.payload
    },
  },
})

export const {
  setLoggingData,
  setForgetPasswordToken,
  setShopifyConnectFirstTimeMessageShow,
  setInitialSyncStatus,
  setIsLoggedIn,
} = authSlice.actions

export default authSlice.reducer
